import { z } from 'zod';
import type { Verkaufsmodell } from './haendler';
import {
  BestellStatus,
  VERSANDDIENSTE,
  type Anschrift,
  type AuszahlungMethode,
  type BelegArt,
  type BestellPosition,
  type BestellungZaehler,
  type BuchungZeile,
} from './bestellungen';

/**
 * Händlerportal Teil 2: Bestellungen, Versand und Auszahlungen aus Sicht des Händlers
 * (mp_my_orders, mp_fulfill, mp_carriers, mp_payouts, mp_payout_pdf, stripe_info/link/dashboard).
 * Der Händler sieht ausschließlich eigene Teilbestellungen und Buchungen.
 */

const Datum = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum als JJJJ-MM-TT');

/* ------------------------------------------------------------------ Bestellungen */

export const PortalBestellungAbfrage = z.object({
  q: z.string().trim().max(100).optional().transform((v) => v || undefined),
  status: BestellStatus.optional(),
  von: Datum.optional(),
  bis: Datum.optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type PortalBestellungAbfrage = z.infer<typeof PortalBestellungAbfrage>;

export interface PortalBestellungZeile {
  id: string;
  nr: string;
  bestelltAm: string;
  kunde: string;
  status: BestellStatus;
  positionen: number;
  /** Cent – Umsatz des Händlers mit dieser Bestellung */
  summe: number;
  /** Provision bzw. Marge zzgl. USt auf Provision */
  abzug: number;
  /** Was der Händler erhält */
  netto: number;
  versandDienst: string;
  sendungsnummer: string;
}

export interface PortalBestellungSeite {
  eintraege: PortalBestellungZeile[];
  weiter: string | null;
  gesamt: number;
  gesamtGenau: boolean;
  zaehler: BestellungZaehler;
}

/** Buchung ohne interne Angaben (wer im Backoffice gebucht hat) */
export type PortalBuchung = Omit<BuchungZeile, 'von'>;

/** Stand der Übertragung an den Marktplatz-Shop: '' = noch nicht gemeldet, entfaellt = Bestellung nicht aus dem Shop */
export type ShopVersandStand = '' | 'uebertragen' | 'ausstehend' | 'entfaellt';

export const SHOP_VERSAND_TITEL: Record<ShopVersandStand, string> = {
  '': 'Noch nicht gemeldet',
  uebertragen: 'An den Marktplatz-Shop übertragen',
  ausstehend: 'Übertragung an den Marktplatz-Shop ausstehend',
  entfaellt: 'Keine Übertragung nötig',
};

export interface PortalBestellungDetail extends PortalBestellungZeile {
  waehrung: string;
  zahlung: string;
  modell: Verkaufsmodell;
  provision: number;
  provisionUst: number;
  /** Lieferanschrift wie im Portal der PHP-Middleware (Firma, Name, Straße, PLZ, Ort, Land) */
  anschrift: Anschrift;
  statusGrund: string;
  statusAm: string | null;
  sendungUrl: string;
  versendetAm: string | null;
  zugestelltAm: string | null;
  /** Auszahlbar ab (JJJJ-MM-TT) */
  faelligAm: string;
  ausgezahlt: { id: string; belegNr: string | null; datum: string } | null;
  positionenListe: BestellPosition[];
  buchungen: PortalBuchung[];
  shopVersand: { stand: ShopVersandStand; hinweis: string; am: string | null };
  /** Versand melden bzw. Sendungsnummer nachtragen möglich */
  kannVersand: boolean;
}

/** Versanddienstleister fürs Formular (mp_carriers) */
export interface PortalVersanddienst {
  id: string;
  name: string;
  /** Mit Sendungsverfolgung – dann ist die Sendungsnummer Pflicht */
  nummerNoetig: boolean;
}

export const portalVersanddienste = (): PortalVersanddienst[] =>
  Object.entries(VERSANDDIENSTE).map(([id, d]) => ({ id, name: d.name, nummerNoetig: d.url !== '' }));

export const PortalVersandEingabe = z
  .object({
    dienst: z.string().trim().max(40).refine((d) => d in VERSANDDIENSTE, 'Bitte einen Versanddienstleister wählen.'),
    sendungsnummer: z.string().trim().max(80, 'Höchstens 80 Zeichen').default(''),
  })
  .superRefine((v, ctx) => {
    const d = VERSANDDIENSTE[v.dienst];
    if (d && d.url !== '' && !v.sendungsnummer) ctx.addIssue({ code: 'custom', path: ['sendungsnummer'], message: 'Bitte die Sendungsnummer eintragen.' });
  });
export type PortalVersandEingabe = z.infer<typeof PortalVersandEingabe>;

export interface PortalVersandErgebnis {
  bestellung: PortalBestellungDetail;
  /** Dieselbe Sendung war schon gemeldet – nichts geändert */
  bereits: boolean;
  /** Bestehende Sendung: nur die Sendungsnummer wurde nachgetragen bzw. geändert */
  nachgetragen: boolean;
}

/* ------------------------------------------------------------------ Auszahlungen */

export interface PortalAuszahlungUebersicht {
  modell: Verkaufsmodell;
  fristTage: number;
  /** Cent: offen und fällig / noch in der Wartefrist / zusammen. Negativ = wird mit künftigen Verkäufen verrechnet. */
  saldo: { verfuegbar: number; wartet: number; gesamt: number };
  /** Vom Marktplatz vorbereitete, noch nicht gebuchte Auszahlung */
  vorbereitet: { betrag: number; datum: string } | null;
  /** Ausgezahlt in den letzten 12 Monaten (Cent) */
  ausgezahlt12Monate: number;
  zuletzt: string | null;
}

export type PortalAuszahlungStatus = 'gebucht' | 'storniert';

export interface PortalAuszahlungZeile {
  id: string;
  datum: string;
  referenz: string;
  /** Cent */
  betrag: number;
  methode: AuszahlungMethode;
  status: PortalAuszahlungStatus;
  belegNr: string | null;
  belegArt: BelegArt | null;
}

export const PortalSeitenAbfrage = z.object({
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export interface PortalAuszahlungSeite {
  eintraege: PortalAuszahlungZeile[];
  weiter: string | null;
}

export interface PortalBuchungSeite {
  eintraege: PortalBuchung[];
  weiter: string | null;
}

/* ------------------------------------------------------------------ Stripe Connect */

export type StripeStufe = 'keins' | 'einrichtung' | 'pruefung' | 'aktiv' | 'fehler';

export const STRIPE_STUFE_TITEL: Record<StripeStufe, string> = {
  keins: 'Nicht verbunden',
  einrichtung: 'Einrichtung offen',
  pruefung: 'In Prüfung bei Stripe',
  aktiv: 'Aktiv – Auszahlungen über Stripe',
  fehler: 'Nicht abrufbar',
};

export interface PortalStripeStatus {
  /** Der Marktplatz hat Stripe Connect eingerichtet */
  eingerichtet: boolean;
  modus: 'test' | 'live' | null;
  stufe: StripeStufe;
  verbunden: boolean;
  /** Angaben bei Stripe vollständig eingereicht */
  details: boolean;
  auszahlungen: boolean;
  /** Anzahl offener Angaben bei Stripe */
  offen: number;
  bank: string;
  fehler: string;
  /** Nur der Inhaber richtet Stripe ein */
  darfVerwalten: boolean;
}

export interface PortalStripeLink {
  url: string;
}

/** Stufen beim gewählten Zahlungsanbieter des Marktplatzes (Stripe, Mollie oder Nuvei) */
export function anbieterStufeTitel(stufe: StripeStufe, name: string): string {
  if (stufe === 'pruefung') return `In Prüfung bei ${name}`;
  if (stufe === 'aktiv') return `Aktiv – Auszahlungen über ${name}`;
  return STRIPE_STUFE_TITEL[stufe];
}

export interface PortalZahlungStatus extends PortalStripeStatus {
  anbieter: 'stripe' | 'mollie' | 'nuvei';
  /** Anzeigename, z. B. „Mollie“ */
  name: string;
  /** Text der Schaltfläche zum Konto beim Anbieter (Dashboard bzw. Angaben ändern) */
  kontoText: string;
}
